import { useMemo } from 'react';

import { ApiNotice } from '#/api/types';
import { useIsMobile } from '#/hooks/useIsMobile';
import { getNotices } from '#/reduxState/notices/noticesReducerUtils';
import { useSelector } from '#/reduxState/store';

/**
 * Captures the appropriate notice to show the user (if any) for the given location/type.
 */
export function useNotices(
  type: 'banners' | 'notifications'
): undefined | { main: ApiNotice; secondary?: ApiNotice } {
  const isMobile = useIsMobile();
  const notices = useSelector(state => state.notices);
  const dismissedNotices = useSelector(
    state => state.userSettings.dismissedNotices
  );
  const bannerNotices = getNotices(notices, type);

  const hasNotices = useMemo(() => {
    return Object.keys(bannerNotices).length > 0;
  }, [bannerNotices]);

  const noticesToShow = useMemo(() => {
    if (!hasNotices) {
      return;
    }
    const sorted = Object.entries(bannerNotices)
      // .filter(
      //   ([, notice]) =>
      //     (!notice.chain && activeChain === 'ethereumChain') ||
      //     notice.chain === activeChain
      // )
      .sort(([, a], [, b]) => b.priority - a.priority);
    const result = sorted
      .map(notice => notice[1])
      .filter(notice => {
        if (
          (isMobile && notice.isDesktopOnly) ||
          (!isMobile && notice.isMobileOnly)
        ) {
          return false;
        }
        // if notice was dismissed but is no longer dismissable it should now be shown again
        if (notice.isDismissable && dismissedNotices.includes(notice.id)) {
          return false;
        }
        return true;
      });
    return result;
  }, [hasNotices, bannerNotices, isMobile, dismissedNotices]);

  return !noticesToShow || noticesToShow?.length === 0
    ? undefined
    : isMobile && noticesToShow?.[0].messageMobile
    ? {
        main: { ...noticesToShow[0], message: noticesToShow[0].messageMobile },
        secondary: noticesToShow[1]
          ? ({
              ...noticesToShow[1],
              message: noticesToShow[1].messageMobile,
            } as ApiNotice)
          : undefined,
      }
    : { main: noticesToShow[0], secondary: noticesToShow[1] };
}
